"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
   Plus,
   Check,
   ChevronDown,
   BicepsFlexed,
   ChartCandlestick,
   Lock,
   RefreshCw
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { cn, getEnvironment } from "@/app/lib/utils";
import { DOMAIN, ENVIRONMENT, PROJECTS } from "@/app/lib/enums";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@/app/components/ui";
import { useAuth } from "@/app/hooks";
import { useMediaQuery, useTenant } from "@/app/hooks";
import { useStateHistoryChatPage } from "@/app/store/chat";
import {
   AiSourceOption,
   initialAiSourceOption,
   useAiSourceSelector
} from "@/app/store/ai-source-selector";

interface AiSourceSelectorProps {
   className?: string;
   hiddenModel?: boolean;
   disabled?: boolean;
}

export const useAiSourceOptions = (): AiSourceOption[] => {
   const translate = useTranslations("Components.AiSourceSelector");
   const { user } = useAuth();
   const locale = useLocale();

   return useMemo(() => {
      const protocol =
         typeof window !== "undefined" ? window.location.protocol + "//" : "https://";
      return [
         {
            id: PROJECTS.CRYPTOCURRENCY_MARKET,
            name: PROJECTS.CRYPTOCURRENCY_MARKET + " - Ai",
            description: translate("cryptocurrencyMarketDescription"),
            isPremium: !user?.projects?.includes(PROJECTS.CRYPTOCURRENCY_MARKET),
            upgradeUrl: protocol + DOMAIN.CRYPTOCURRENCY_MARKET + "/shop",
            icon: "ChartCandlestick"
         },
         {
            id: PROJECTS.KINGS_AND_ALLIES,
            name: PROJECTS.KINGS_AND_ALLIES + " - Ai",
            description: translate("kingsAndAlliesDescription"),
            isPremium: !user?.projects?.includes(PROJECTS.KINGS_AND_ALLIES),
            upgradeUrl: `${protocol}${DOMAIN.KINGS_AND_ALLIES}/${locale}/shop`,
            icon: "BicepsFlexed"
         }
      ];
   }, [user?.projects, locale, translate]);
};

export const AiSourceSelector = (props: AiSourceSelectorProps) => {
   const { className = "text-lg", hiddenModel = false, disabled = false } = props;
   const translate = useTranslations("Components.AiSourceSelector");
   const router = useRouter();
   const [isOpen, setIsOpen] = useState(false);
   const { user, checkAuth } = useAuth();
   const [isRechecking, setIsRechecking] = useState(false);
   const tenant = useTenant();
   const isMobile = useMediaQuery("(max-width: 768px)");
   const { selectedModel, resetState, historyItem } = useStateHistoryChatPage();

   const { selectedBaseKnowledge, setSelectedBaseKnowledge } = useAiSourceSelector();

   const aiSourceOptions = useAiSourceOptions();

   const hasLoadedProjects = Array.isArray(user?.projects);

   const hasNoAccess = useMemo(
      () => hasLoadedProjects && aiSourceOptions.every((option) => option.isPremium),
      [aiSourceOptions, hasLoadedProjects]
   );

   const hasNoAccessRef = useRef(hasNoAccess);
   hasNoAccessRef.current = hasNoAccess;

   // Auto re-check access when user returns to tab while in no-access state
   useEffect(() => {
      const handleVisibilityChange = async () => {
         if (document.visibilityState === "visible" && hasNoAccessRef.current) {
            await checkAuth({ forceRefresh: true });
         }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () =>
         document.removeEventListener("visibilitychange", handleVisibilityChange);
   }, [checkAuth]);

   const handleRecheck = useCallback(async () => {
      setIsRechecking(true);
      try {
         await checkAuth({ forceRefresh: true });
      } finally {
         setIsRechecking(false);
      }
   }, [checkAuth]);

   // Read knowledgeBase from query param (for cross-domain navigation)
   useEffect(() => {
      if (typeof window === "undefined") return;
      const urlParams = new URLSearchParams(window.location.search);
      const knowledgeBaseParam = urlParams.get("knowledgeBase");

      if (knowledgeBaseParam && aiSourceOptions.length > 0) {
         const matchedOption = aiSourceOptions.find(
            (option) => option.id === knowledgeBaseParam
         );
         if (matchedOption && !matchedOption.isPremium) {
            setSelectedBaseKnowledge(matchedOption);
            // Clean up URL after reading the param
            urlParams.delete("knowledgeBase");
            const newUrl =
               window.location.pathname +
               (urlParams.toString() ? "?" + urlParams.toString() : "");
            window.history.replaceState({}, "", newUrl);
         }
      }
   }, [aiSourceOptions, setSelectedBaseKnowledge]);

   useEffect(() => {
      if (user?.projects?.length && tenant?.project && aiSourceOptions.length > 0) {
         // Automatically switches when conversation changes
         const automaticallySwitchesWhenConversationChanges =
            historyItem?.messages?.items?.length;
         if (automaticallySwitchesWhenConversationChanges) {
            const knowledgeBase = historyItem?.messages?.items?.find(
               (option) => option.knowledgeBase?.length
            );
            if (knowledgeBase && knowledgeBase.knowledgeBase?.length) {
               const knowledgeBaseOption = aiSourceOptions.find(
                  (option) => option.id === (knowledgeBase.knowledgeBase?.[0] as PROJECTS)
               );
               if (knowledgeBaseOption) {
                  setSelectedBaseKnowledge(knowledgeBaseOption);
               }
            }
         }

         // Auto-switch if currently selected project lost access (e.g. config changed)
         if (selectedBaseKnowledge.id) {
            const currentOption = aiSourceOptions.find(
               (option) => option.id === selectedBaseKnowledge.id
            );
            if (currentOption?.isPremium) {
               // Selected project is no longer accessible — switch to first available
               const fallbackOption =
                  aiSourceOptions.find(
                     (option) =>
                        option.id === tenant.project &&
                        user?.projects?.includes(option.id as PROJECTS)
                  ) ||
                  aiSourceOptions.find((option) =>
                     user?.projects?.includes(option.id as PROJECTS)
                  );
               setSelectedBaseKnowledge(fallbackOption || initialAiSourceOption);
               return;
            }
            return;
         }

         const defaultOption =
            aiSourceOptions.find(
               (option) =>
                  option.id === tenant.project &&
                  user?.projects?.includes(option.id as PROJECTS)
            ) ||
            aiSourceOptions.find((option) =>
               user?.projects?.includes(option.id as PROJECTS)
            );

         setSelectedBaseKnowledge(defaultOption || initialAiSourceOption);
      }
   }, [user?.projects, tenant?.project, historyItem?.messages?.items]);

   const handleUpgrade = (e: React.MouseEvent, url: string) => {
      e.stopPropagation();
      window.open(url, "_blank");
   };

   const renderIcon = useMemo(() => {
      const iconMap = {
         ChartCandlestick: <ChartCandlestick className="h-5 w-5" />,
         BicepsFlexed: <BicepsFlexed className="h-5 w-5" />,
         Plus: <Plus className="h-5 w-5" />
      };

      return (iconType: string) => iconMap[iconType as keyof typeof iconMap] || null;
   }, []);
   const isSelected = useCallback(
      (option: AiSourceOption) => {
         return selectedBaseKnowledge.id === option.id && !option.isPremium;
      },
      [selectedBaseKnowledge.id]
   );
   const switchKnowledgeBase = useCallback(
      (environment: ENVIRONMENT, project: PROJECTS): string => {
         const protocol =
            typeof window !== "undefined" ? window.location.protocol + "//" : "https://";
         const objectDomain = {
            [ENVIRONMENT.DEVELOPMENT]: {
               [PROJECTS.CRYPTOCURRENCY_MARKET]: "/",
               [PROJECTS.KINGS_AND_ALLIES]: "/"
            },
            [ENVIRONMENT.STAGING]: {
               [PROJECTS.CRYPTOCURRENCY_MARKET]:
                  protocol + DOMAIN.CRYPTOCURRENCY_MARKET_STAGING_FRONTEND,
               [PROJECTS.KINGS_AND_ALLIES]:
                  protocol + DOMAIN.KINGS_AND_ALLIES_STAGING_FRONTEND
            },
            [ENVIRONMENT.PRODUCTION]: {
               [PROJECTS.CRYPTOCURRENCY_MARKET]:
                  protocol + DOMAIN.CRYPTOCURRENCY_MARKET_FRONTEND,
               [PROJECTS.KINGS_AND_ALLIES]:
                  protocol + DOMAIN.KINGS_AND_ALLIES_PRODUCTION_FRONTEND
            }
         };
         return objectDomain[environment][project];
      },
      []
   );
   const handleNewChat = (option: AiSourceOption) => {
      resetState();
      setIsOpen(false);
      const environment = getEnvironment();
      const directDomain = switchKnowledgeBase(environment, option.id as PROJECTS);

      const isDifferentDomain =
         directDomain.startsWith("http") &&
         !directDomain.startsWith(window.location.origin);

      if (isDifferentDomain) {
         const url = new URL(directDomain);
         url.searchParams.set("knowledgeBase", option.id);
         return (window.location.href = url.toString());
      }

      setSelectedBaseKnowledge(option);
      router.push(directDomain);
   };

   return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
         <PopoverTrigger asChild>
            <Button
               variant="ghost"
               className={cn(
                  "h-12 w-full justify-between px-4 py-3 font-normal",
                  className
               )}
               disabled={disabled}
            >
               <span
                  className={cn(
                     "font-medium relative flex items-center gap-2",
                     isMobile && "min-w-0 flex-1"
                  )}
               >
                  {hasNoAccess && (
                     <Lock
                        className={cn("h-4 w-4 text-amber-500", isMobile && "shrink-0")}
                     />
                  )}
                  <span className={cn(isMobile && "min-w-0 flex-1 truncate text-left")}>
                     {hasNoAccess
                        ? translate("noAccessTitle")
                        : selectedBaseKnowledge?.name}
                  </span>
                  <div
                     className={cn(
                        "absolute top-4",
                        className.match(/text-(?:\[[^\]]+\]|[a-z0-9-]+)/i)?.[0] || "",
                        (hiddenModel || hasNoAccess) && "hidden"
                     )}
                  >
                     {user?.isAdmin && !isMobile && (
                        <span className="text-gray-500 text-[10px]">
                           <span>model: </span>
                           <code>{selectedModel}</code>
                        </span>
                     )}
                  </div>
               </span>

               {!disabled && (
                  <ChevronDown
                     className={cn(
                        "h-4 w-4 transition-transform text-muted-foreground",
                        isMobile && "shrink-0",
                        isOpen && "rotate-180"
                     )}
                  />
               )}
            </Button>
         </PopoverTrigger>
         <PopoverContent className="w-[390px] p-2" align="start">
            {hasNoAccess && (
               <div className="p-3 mb-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center gap-2 mb-1">
                     <Lock className="h-4 w-4 text-amber-500" />
                     <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                        {translate("noAccessTitle")}
                     </span>
                     <Button
                        variant="ghost"
                        size="xs"
                        className="ml-auto h-6 px-2 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                        onClick={handleRecheck}
                        disabled={isRechecking}
                     >
                        <RefreshCw
                           className={cn("h-3 w-3 mr-1", isRechecking && "animate-spin")}
                        />
                        {translate("recheck")}
                     </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                     {translate("noAccessDescription")}
                  </p>
               </div>
            )}
            {aiSourceOptions.map((option) => (
               <div
                  key={option.id}
                  className="flex items-center justify-between p-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                  onClick={(e) => {
                     if (isSelected(option)) return setIsOpen(false);
                     if (option.isPremium) {
                        return handleUpgrade(e as React.MouseEvent, option.upgradeUrl);
                     }
                     return handleNewChat(option);
                  }}
               >
                  <div className="flex items-center gap-3 flex-1">
                     <div className="text-muted-foreground">
                        {renderIcon(option.icon)}
                     </div>
                     <div className="flex-1">
                        <div className="font-medium text-popover-foreground">
                           {option.name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                           {option.description}
                        </div>
                     </div>
                  </div>
                  <div className="ml-1">
                     {option.isPremium && (
                        <Button
                           variant="outline"
                           size="sm"
                           onClick={(e) => handleUpgrade(e, option.upgradeUrl)}
                           className="h-6 px-2 text-xs rounded-full"
                        >
                           {translate("upgrade")}
                        </Button>
                     )}
                     {!option.isPremium && !isSelected(option) && (
                        <Button
                           variant="outline"
                           size="sm"
                           className="h-6 px-2 text-xs rounded-full"
                        >
                           {translate("newChat")}
                        </Button>
                     )}
                     {isSelected(option) && (
                        <Check className="h-5 w-5 text-muted-foreground" />
                     )}
                  </div>
               </div>
            ))}
         </PopoverContent>
      </Popover>
   );
};
